import React from 'react';
import { Eye } from "lucide-react";
import { DirectPreviewForm } from './DirectPreviewForm';

interface Question {
  id: string;
  type: string;
  text: string;
  required: boolean;
  options?: { id: string; value: string }[];
}

interface UITextConfig {
  ratingTitle: string;
  ratingSubtitle: string;
  ratingCTA: string;
  rewardTitle: string;
  feedbackTitle: string;
  emailLabel: string;
  emailPlaceholder: string;
  consentText: string;
  continueButtonText: string;
  nextButtonText: string;
  formTitle: string;
  nameLabel: string;
  namePlaceholder: string;
  submitButtonText: string;
  successTitle: string;
  successMessageHigh: string;
  successMessageLow: string;
}

interface PreviewContainerProps {
  questions: Question[];
  starThreshold?: number;
  rewardEnabled?: boolean; // Legacy - keep for backwards compatibility
  enableStep2?: boolean; // Legacy - keep for backwards compatibility
  enableStep2High?: boolean; // New - enable step 2 for high ratings
  enableStep2Low?: boolean; // New - enable step 2 for low ratings
  highRatingEmailEnabled?: boolean; // High rating email collection
  highRatingEmailRequired?: boolean; // High rating email required
  highRatingRewardEnabled?: boolean; // High rating reward system
  lowRatingEmailEnabled?: boolean; // Low rating email collection
  lowRatingEmailRequired?: boolean; // Low rating email required
  lowRatingRewardEnabled?: boolean; // Low rating reward system
  previewHighRating?: boolean; // Controls which rating scenario to preview
  businessName?: string;
  formId?: string;
  business?: any;
  onUpdateQuestion?: (id: string, field: keyof Question, value: unknown) => void;
  onAddOption?: (questionId: string) => void;
  onUpdateOption?: (questionId: string, optionId: string, value: string) => void;
  onRemoveOption?: (questionId: string, optionId: string) => void;
  editingEnabled?: boolean;
  uiTextConfig?: UITextConfig;
  onUpdateUIText?: (field: keyof UITextConfig, value: string) => void;
  processUIText?: (text: string, businessName?: string) => string;
  previewStep?: number;
  onPreviewStepChange?: (step: number) => void;
}

// Optimized mobile size for sidebar preview - iPhone 15 Pro Max proportions
const MOBILE_SIZE = { width: 320, height: 590, label: 'iPhone 15 Pro Max' };

export const PreviewContainer: React.FC<PreviewContainerProps> = ({
  questions,
  starThreshold = 4,
  rewardEnabled = false,
  enableStep2 = true,
  enableStep2High,
  enableStep2Low,
  highRatingEmailEnabled = false,
  highRatingEmailRequired = false,
  highRatingRewardEnabled = false,
  lowRatingEmailEnabled = false,
  lowRatingEmailRequired = false,
  lowRatingRewardEnabled = false,
  previewHighRating = true,
  businessName = "Your Business",
  editingEnabled = false,
  uiTextConfig,
  onUpdateUIText,
  onUpdateQuestion,
  previewStep = 1,
  onPreviewStepChange
}) => {
  const currentDevice = MOBILE_SIZE;
  
  // Calculate simulated rating based on previewHighRating toggle
  const simulatedRating = previewHighRating 
    ? Math.min(starThreshold + 1, 5) // High rating: threshold + 1 (max 5)
    : Math.max(starThreshold - 1, 1); // Low rating: threshold - 1 (min 1)

  return (
    <div className="space-y-2">
      {/* iPhone - Minimal */}
      <div className="flex justify-center pt-4">
        <div className="relative bg-gradient-to-b from-gray-400 to-gray-500 rounded-[2rem] p-1.5 shadow-2xl"
             style={{ width: currentDevice.width + 12, height: currentDevice.height + 12 }}>
          {/* Screen */}
          <div className="relative w-full h-full bg-white dark:bg-background rounded-[1.75rem] overflow-hidden"
               style={{ width: currentDevice.width, height: currentDevice.height }}>
                    {/* Status Bar */}
                    <div className="absolute top-0 left-0 right-0 h-8 bg-white dark:bg-gray-800 flex items-center justify-between px-4 z-50 text-gray-900 dark:text-gray-100 text-xs font-medium">
                      <div className="flex items-center">
                        <span>9:41</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Signal bars - Full signal (4 bars) */}
                        <div className="flex gap-[2px] items-end">
                          <div className="w-1 h-1.5 bg-current rounded-full"></div>
                          <div className="w-1 h-2 bg-current rounded-full"></div>
                          <div className="w-1 h-2.5 bg-current rounded-full"></div>
                          <div className="w-1 h-3 bg-current rounded-full"></div>
                        </div>
                        {/* WiFi icon - 2 out of 3 bars */}
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 20h.01M8.5 16.5a5 5 0 017 0M5 13a9 9 0 0114 0" opacity="0.4" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.5 16.5a5 5 0 017 0M12 20h.01" />
                        </svg>
                        {/* Battery - Gray/not charging */}
                        <div className="w-5 h-2.5 border-2 border-current rounded-[3px] relative">
                          <div className="absolute -right-[3px] top-1/2 -translate-y-1/2 w-[2px] h-1.5 bg-current rounded-r-sm"></div>
                          <div className="w-3/4 h-full bg-gray-400 dark:bg-gray-500 rounded-[1px]"></div>
                        </div>
                      </div>
                    </div>

                    {/* Content Area - Fixed Height with Scrolling */}
                    <div className="absolute top-8 bottom-0 left-0 right-0">
                      <div className="w-full h-full overflow-auto">
                        <DirectPreviewForm
                          questions={questions}
                          starThreshold={starThreshold}
                          rewardEnabled={rewardEnabled}
                          enableStep2={enableStep2}
                          enableStep2High={enableStep2High}
                          enableStep2Low={enableStep2Low}
                          highRatingEmailEnabled={highRatingEmailEnabled}
                          highRatingEmailRequired={highRatingEmailRequired}
                          highRatingRewardEnabled={highRatingRewardEnabled}
                          lowRatingEmailEnabled={lowRatingEmailEnabled}
                          lowRatingEmailRequired={lowRatingEmailRequired}
                          lowRatingRewardEnabled={lowRatingRewardEnabled}
                          businessName={businessName}
                          editingEnabled={editingEnabled}
                          uiTextConfig={uiTextConfig}
                          onUpdateUIText={onUpdateUIText}
                          onUpdateQuestion={onUpdateQuestion}
                          initialStep={previewStep}
                          onStepChange={onPreviewStepChange}
                          simulatedRating={simulatedRating}
                        />
                      </div>
                    </div>
          </div>
        </div>
      </div>

      {/* Help hint - only show when editing is enabled */}
      {editingEnabled && (
        <div className="flex items-center justify-center gap-1.5">
          <svg className="w-3.5 h-3.5 flex-shrink-0 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-blue-600 dark:text-blue-400">Click any text to customize</p>
        </div>
      )}
    </div>
  );
};

export default PreviewContainer;